/**
 * ProductBomSpreadsheetView.tsx
 *
 * Full-screen embedded BOM spreadsheet matching View 2 in product-bom-mapping-demo.html.
 * Features:
 * - Specification Header Grid (product & BOM config info)
 * - Multi-cell range selection like Excel
 * - Copy/Paste with Excel (TSV format)
 * - Blank row creation ("Khi tạo mới mặc định trống")
 * - Edit mode lock ("Ấn vào sửa thì mới được sửa")
 * - Dynamic CBM pills per wood type & grand total
 * - Signatures block
 * - A4 Portrait print & PDF export (absolute 210mm×297mm; no landscape)
 * - Excel import & export (SheetJS)
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { assertWorkbookFileSize, readSafeWorkbook, validateWorkbookBytes } from '@/lib/safeWorkbook'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  FileSpreadsheet,
  GitCompare,
  History,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Printer,
  Redo2,
  RotateCcw,
  Save,
  Trash2,
  Undo2,
  Upload,
  X,
} from 'lucide-react'
import { moveRowUp, moveRowDown } from '@/components/table/ExcelEditableTable'
import type {
  BomConfigRow,
  BomSpreadsheetWoodRow,
  BomSpreadsheetHardwareRow,
  ProductBomSpreadsheetData,
  BomRevision,
} from '../types/bomSpreadsheetTypes'
import {
  createBlankWoodRow,
  createBlankHardwareRow,
  calculateWoodRowCbm,
} from '../types/bomSpreadsheetTypes'
import {
  compareBomData,
  loadProductBomRevisions,
  saveProductBomRevisions,
} from '../services/bomSpreadsheetStorage'
import {
  getProductBomRevisionsApi,
  createProductBomRevisionApi,
} from '@/services/boms/productBomSpreadsheetApiClient'
import { ProductBomSpreadsheetCompareModal } from './ProductBomSpreadsheetCompareModal'
import { MaterialMappingModal } from './MaterialMappingModal'
import { resolvePriceBatchApi } from '@/services/procurement/apiClient'
import type { ResolvedPurchasePrice } from '@/services/procurement/types'
import { Link2 } from 'lucide-react'
import { formatDate } from '@/lib/dateFormat'
import { parseBomSpreadsheetExcelRows } from '../bomExcelImport'

interface ProductBomSpreadsheetViewProps {
  productId?: string
  productCode: string
  productName: string
  productImageUrl?: string | null
  customer?: string
  dimensionsMm: { w: number; d: number; h: number }
  dimensionsInch: { w: number; d: number; h: number }
  colorNotes?: string
  activeConfig: BomConfigRow
  allConfigs?: BomConfigRow[]
  initialData: ProductBomSpreadsheetData
  onSaveData: (data: ProductBomSpreadsheetData) => void
  onBack: () => void
}

export type ActiveTableType = 'wood' | 'hardware'

interface CellSelection {
  table: ActiveTableType
  startRow: number
  startCol: number
  endRow: number
  endCol: number
  activeRow: number
  activeCol: number
}

const WOOD_COLS: { key: keyof BomSpreadsheetWoodRow | 'cbm'; label: string; type: 'text' | 'number' | 'calc' }[] = [
  { key: 'part_name', label: 'Tên chi tiết', type: 'text' },
  { key: 'wood_type_raw', label: 'Loại gỗ', type: 'text' },
  { key: 'dims_finished_t', label: 'Dày', type: 'number' },
  { key: 'dims_finished_w', label: 'Rộng', type: 'number' },
  { key: 'dims_finished_l', label: 'Dài', type: 'number' },
  { key: 'tenon', label: 'Mộng', type: 'text' },
  { key: 'qty_per', label: 'SL 1SP', type: 'number' },
  { key: 'qty_total', label: 'SL Tổng', type: 'calc' },
  { key: 'notes', label: 'Ghi chú', type: 'text' },
  { key: 'veneer_spec', label: 'Veneer', type: 'text' },
  { key: 'cbm', label: 'Khối lượng (M³)', type: 'calc' },
]

const HW_COLS: { key: keyof BomSpreadsheetHardwareRow; label: string; type: 'text' | 'number' | 'calc' }[] = [
  { key: 'name_raw', label: 'Tên vật tư', type: 'text' },
  { key: 'spec_raw', label: 'Quy cách', type: 'text' },
  { key: 'qty_per', label: 'SL', type: 'number' },
  { key: 'qty_total', label: 'SL Tổng', type: 'calc' },
  { key: 'uom_hint', label: 'ĐVT', type: 'text' },
  { key: 'color_hint', label: 'Màu', type: 'text' },
  { key: 'item_type', label: 'Phân loại', type: 'text' },
  { key: 'notes', label: 'Ghi chú', type: 'text' },
]

/** Print classes to map each table cell strictly to its leaf column */
const WOOD_COL_PRINT_CLASSES: Record<string, string> = {
  part_name: 'col-wood-part',
  wood_type_raw: 'col-wood-type',
  dims_finished_t: 'col-wood-t',
  dims_finished_w: 'col-wood-w',
  dims_finished_l: 'col-wood-l',
  tenon: 'col-wood-tenon',
  qty_per: 'col-wood-qty-per',
  qty_total: 'print:hidden no-print',
  notes: 'col-wood-notes',
  veneer_spec: 'col-wood-veneer',
  cbm: 'col-wood-cbm',
}

const HW_COL_PRINT_CLASSES: Record<string, string> = {
  name_raw: 'col-hw-name',
  spec_raw: 'col-hw-spec',
  qty_per: 'col-hw-qty',
  qty_total: 'print:hidden no-print',
  uom_hint: 'col-hw-uom',
  color_hint: 'col-hw-color',
  item_type: 'col-hw-type',
  notes: 'col-hw-notes',
}

/** Print/PDF: hide selection chrome; row STT keeps readable neutrals. */
const PRINT_STT_ROW_SELECTED =
  'print:bg-slate-50 print:text-slate-700 print:shadow-none'
const PRINT_CELL_SELECTION_RING = 'print:ring-0'

function DataRouterBlocker({ when }: { when: boolean }) {
  const blocker = useBlocker(when)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmLeave = window.confirm(
        'Bạn có thay đổi chưa lưu, chắc chắn muốn rời trang?',
      )
      if (confirmLeave) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  return null
}

function SafeBlocker({ when }: { when: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  if (!dataRouter) return null
  return <DataRouterBlocker when={when} />
}

/** Pure dirty check — same idea as quote `isQuoteRowsDirty`. */
export function isBomSpreadsheetDirty(
  current: ProductBomSpreadsheetData,
  baseline: ProductBomSpreadsheetData | null,
  isEditMode: boolean,
): boolean {
  if (!isEditMode || !baseline) return false
  const diffs = compareBomData(
    baseline.wood || [],
    current.wood || [],
    baseline.hardware || [],
    current.hardware || [],
  )
  return diffs.length > 0
}

/** Nạp Excel always replaces both tables (clear old wood/hardware, then set parsed). */
export function applyParsedBomExcelImport(
  current: ProductBomSpreadsheetData,
  parsed: { wood: BomSpreadsheetWoodRow[]; hardware: BomSpreadsheetHardwareRow[] },
): ProductBomSpreadsheetData {
  return {
    ...current,
    wood: parsed.wood,
    hardware: parsed.hardware,
  }
}

export function moveBomSpreadsheetRow(
  data: ProductBomSpreadsheetData,
  table: ActiveTableType,
  index: number,
  direction: 'up' | 'down',
): ProductBomSpreadsheetData {
  const mover = direction === 'up' ? moveRowUp : moveRowDown
  const next = { ...data }
  if (table === 'wood') {
    next.wood = mover(next.wood, index)
  } else {
    next.hardware = mover(next.hardware, index)
  }
  return next
}

export function ProductBomSpreadsheetView({
  productId,
  productCode,
  productName,
  productImageUrl,
  customer = 'Williams Sonoma / Crate & Barrel (Thị trường Mỹ & EU)',
  dimensionsMm,
  dimensionsInch,
  colorNotes = '',
  activeConfig,
  allConfigs = [activeConfig],
  initialData,
  onSaveData,
  onBack,
}: ProductBomSpreadsheetViewProps) {
  // Master data
  const [data, setData] = useState<ProductBomSpreadsheetData>(initialData)
  const [isEditMode, setIsEditMode] = useState(false)

  useEffect(() => {
    if (!isEditMode) {
      setData(initialData)
    }
  }, [initialData, isEditMode])
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  /** Set only around window.print — drives .print-portrait CSS; cleared on afterprint. */
  const [printLayout, setPrintLayout] = useState<'portrait' | null>(null)
  const [isPrintMedia, setIsPrintMedia] = useState(false)
  const [showCompareModal, setShowCompareModal] = useState(false)

  useEffect(() => {
    const media = window.matchMedia('print')
    const syncPrintMedia = () => setIsPrintMedia(media.matches)
    syncPrintMedia()
    media.addEventListener('change', syncPrintMedia)
    return () => media.removeEventListener('change', syncPrintMedia)
  }, [])

  // Order quantity (defaults to 1, editable without entering edit mode, multiplies SL Tổng)
  const [orderQty, setOrderQty] = useState<number>(1)

  // Hardware pricing state
  const [hardwarePrices, setHardwarePrices] = useState<Record<string, ResolvedPurchasePrice>>({})
  const [mappingRowIndex, setMappingRowIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!data.hardware || data.hardware.length === 0) {
      setHardwarePrices({})
      return
    }

    let active = true
    const items = data.hardware.map((hw) => ({
      materialId: hw.material_id || null,
      supplierId: hw.supplier_id || null,
      qty: typeof hw.qty_per === 'number' ? hw.qty_per : parseFloat(String(hw.qty_per || '1')),
      allowEstimate: true,
    }))

    resolvePriceBatchApi(items)
      .then((results) => {
        if (!active) return
        const priceMap: Record<string, ResolvedPurchasePrice> = {}
        data.hardware.forEach((hw, idx) => {
          const key = hw.id || String(idx)
          if (results[idx]) {
            priceMap[key] = results[idx]
          }
        })
        setHardwarePrices(priceMap)
      })
      .catch((err) => {
        console.error('Lỗi tính giá ngũ kim từ server:', err)
      })

    return () => {
      active = false
    }
  }, [data.hardware])

  const { totalHwMaterialCost, totalHwVatAmount, grandTotalHwWithVat, effectiveHwVatRate, mappedCount } = useMemo(() => {
    let subtotal = 0
    let vat = 0
    let mapped = 0
    let maxVatRate = 8
    data.hardware.forEach((hw, idx) => {
      const key = hw.id || String(idx)
      const priceRes = hardwarePrices[key]
      const qty = typeof hw.qty_per === 'number' ? hw.qty_per : parseFloat(String(hw.qty_per || '0')) || 0
      if (hw.material_id) mapped += 1
      if (priceRes?.status === 'MATCHED' && typeof priceRes.unitPrice === 'number') {
        const lineTotal = qty * priceRes.unitPrice
        subtotal += lineTotal
        const rate = typeof priceRes.vatRateValue === 'number' ? priceRes.vatRateValue : 8
        if (rate > maxVatRate) maxVatRate = rate
        vat += lineTotal * (rate / 100)
      }
    })
    return {
      totalHwMaterialCost: subtotal,
      totalHwVatAmount: vat,
      grandTotalHwWithVat: subtotal + vat,
      effectiveHwVatRate: maxVatRate,
      mappedCount: mapped,
    }
  }, [data.hardware, hardwarePrices])

  // Revision management
  const storageId = productId || productCode
  const [revisions, setRevisions] = useState<BomRevision[]>(() => loadProductBomRevisions(storageId))
  const [currentRev, setCurrentRev] = useState<BomRevision>(() => {
    const revs = loadProductBomRevisions(storageId)
    return (
      revs[revs.length - 1] || {
        id: `rev-init-${storageId}`,
        revNumber: 1,
        revLabel: 'Rev. 01',
        savedAt: formatDate(new Date()),
        savedTime: '08:00',
        author: 'Hà Phan Thanh Tâm',
        changesSummary: 'Phiên bản khởi tạo ban đầu',
        diffs: [],
        snapshot: { wood: initialData.wood, hardware: initialData.hardware },
      }
    )
  })

  // Load revisions from backend API if productId is present
  useEffect(() => {
    if (!productId) return
    let isCancelled = false
    getProductBomRevisionsApi(productId, activeConfig.id)
      .then((revs) => {
        if (!isCancelled && revs.length > 0) {
          setRevisions(revs)
          setCurrentRev(revs[revs.length - 1])
        }
      })
      .catch(() => {})
    return () => {
      isCancelled = true
    }
  }, [productId, activeConfig.id])

  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [expandedRevId, setExpandedRevId] = useState<string | null>(null)
  const editBaselineRef = useRef<ProductBomSpreadsheetData | null>(null)

  // If parent reuses this instance across product/config (no unmount), reset sheet data.
  // Parent also keys by productId+configId; this is a safety net.
  useEffect(() => {
    setData(initialData)
    setIsEditMode(false)
    setOrderQty(1)
    editBaselineRef.current = null
    const revs = loadProductBomRevisions(productId || productCode)
    setRevisions(revs)
    setCurrentRev(
      revs[revs.length - 1] || {
        id: `rev-init-${productId || productCode}`,
        revNumber: 1,
        revLabel: 'Rev. 01',
        savedAt: formatDate(new Date()),
        savedTime: '08:00',
        author: 'Hà Phan Thanh Tâm',
        changesSummary: 'Phiên bản khởi tạo ban đầu',
        diffs: [],
        snapshot: { wood: initialData.wood, hardware: initialData.hardware },
      },
    )
    // intentionally: only product/config switch — not every parent bomData rewrite (would kick out of edit mode)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialData read on product/config boundary only
  }, [productId, productCode, activeConfig.id])

  const hasUnsavedChanges = useMemo(
    () => isBomSpreadsheetDirty(data, editBaselineRef.current, isEditMode),
    [isEditMode, data],
  )

  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  function handleBack() {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'Bạn có thay đổi chưa lưu, chắc chắn muốn rời trang?',
      )
      if (!confirmLeave) return
    }
    onBack()
  }

  // Selection state
  const [selection, setSelection] = useState<CellSelection>({
    table: 'wood',
    startRow: 0,
    startCol: 0,
    endRow: 0,
    endCol: 0,
    activeRow: 0,
    activeCol: 0,
  })
  const [isSelecting, setIsSelecting] = useState(false)
  const [editingCell, setEditingCell] = useState<{ table: ActiveTableType; row: number; col: number } | null>(null)

  // History for Undo/Redo
  const [history, setHistory] = useState<ProductBomSpreadsheetData[]>([])
  const [redoList, setRedoList] = useState<ProductBomSpreadsheetData[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const cellInputRef = useRef<HTMLInputElement>(null)
  const editingCellRef = useRef(editingCell)
  editingCellRef.current = editingCell

  function showToast(msg: string) {
    setToastMsg(msg)
    setTimeout(() => {
      setToastMsg((cur) => (cur === msg ? null : cur))
    }, 3000)
  }

  const pushHistory = useCallback((nextData: ProductBomSpreadsheetData) => {
    setHistory((prev) => [...prev.slice(-30), data])
    setRedoList([])
    setData(nextData)
  }, [data])

  function handleUndo() {
    if (history.length === 0) return
    const prev = history[history.length - 1]
    setHistory((h) => h.slice(0, -1))
    setRedoList((r) => [data, ...r])
    setData(prev)
    showToast('↶ Đã hoàn tác (Undo)')
  }

  function handleRedo() {
    if (redoList.length === 0) return
    const next = redoList[0]
    setRedoList((r) => r.slice(1))
    setHistory((h) => [...h, data])
    setData(next)
    showToast('↷ Đã làm lại (Redo)')
  }

  // Toggle edit / save
  function handleToggleEdit() {
    if (!isEditMode) {
      editBaselineRef.current = JSON.parse(JSON.stringify(data))
      setIsEditMode(true)
      showToast('✏️ Đã mở chế độ chỉnh sửa! Bạn có thể sửa ô, paste từ Excel (Ctrl+V) hoặc xóa dòng.')
    } else {
      setEditingCell(null)
      setIsEditMode(false)

      const baseline =
        editBaselineRef.current ||
        revisions[revisions.length - 1]?.snapshot || { wood: [], hardware: [] }

      const diffs = compareBomData(
        baseline.wood || [],
        data.wood || [],
        baseline.hardware || [],
        data.hardware || [],
      )

      if (diffs.length > 0) {
        const nextRevNum = (currentRev?.revNumber || revisions.length || 1) + 1
        const nextRevLabel = `Rev. ${nextRevNum < 10 ? '0' + nextRevNum : nextRevNum}`
        const todayStr = formatDate(new Date())
        const now = new Date()
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

        const addedCount = diffs.filter((d) => d.type === 'row_added').length
        const deletedCount = diffs.filter((d) => d.type === 'row_deleted').length
        const editCount = diffs.filter((d) => d.type === 'cell_change').length
        const summaryParts: string[] = []
        if (addedCount > 0) summaryParts.push(`Thêm ${addedCount} dòng`)
        if (deletedCount > 0) summaryParts.push(`Xóa ${deletedCount} dòng`)
        if (editCount > 0) summaryParts.push(`Sửa ${editCount} ô`)

        const newRev: BomRevision = {
          id: `rev-${Date.now()}`,
          revNumber: nextRevNum,
          revLabel: nextRevLabel,
          savedAt: todayStr,
          savedTime: timeStr,
          author: 'Hà Phan Thanh Tâm',
          changesSummary: summaryParts.join(', ') || `${diffs.length} thay đổi`,
          diffs,
          snapshot: {
            wood: JSON.parse(JSON.stringify(data.wood)),
            hardware: JSON.parse(JSON.stringify(data.hardware)),
          },
        }

        const nextRevs = [...revisions, newRev]
        setRevisions(nextRevs)
        setCurrentRev(newRev)
        saveProductBomRevisions(storageId, nextRevs)
        if (productId) {
          void createProductBomRevisionApi(productId, activeConfig.id, newRev).catch(() => {})
        }
        showToast(`💾 ✓ Đã lưu BOM! Phiên bản nhảy lên: ${nextRevLabel} (${todayStr})`)
      } else {
        showToast('💾 ✓ Đã lưu toàn bộ thay đổi bảng BOM thành công!')
      }

      editBaselineRef.current = null
      onSaveData(data)
    }
  }

  function handleRestoreRevision(rev: BomRevision) {
    const isConfirm = window.confirm(
      `Bạn có chắc chắn muốn khôi phục dữ liệu BOM về phiên bản ${rev.revLabel} (${rev.savedAt}) không?`,
    )
    if (!isConfirm) return

    const restoredData: ProductBomSpreadsheetData = {
      ...data,
      wood: JSON.parse(JSON.stringify(rev.snapshot.wood || [])),
      hardware: JSON.parse(JSON.stringify(rev.snapshot.hardware || [])),
    }

    const nextRevNum = (currentRev?.revNumber || revisions.length || 1) + 1
    const nextRevLabel = `Rev. ${nextRevNum < 10 ? '0' + nextRevNum : nextRevNum}`
    const todayStr = formatDate(new Date())
    const now = new Date()
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

    const newRev: BomRevision = {
      id: `rev-${Date.now()}`,
      revNumber: nextRevNum,
      revLabel: nextRevLabel,
      savedAt: todayStr,
      savedTime: timeStr,
      author: 'Hà Phan Thanh Tâm',
      changesSummary: `Khôi phục từ ${rev.revLabel} (${rev.savedAt})`,
      diffs: [],
      snapshot: {
        wood: JSON.parse(JSON.stringify(restoredData.wood)),
        hardware: JSON.parse(JSON.stringify(restoredData.hardware)),
      },
    }

    const nextRevs = [...revisions, newRev]
    setRevisions(nextRevs)
    setCurrentRev(newRev)
    saveProductBomRevisions(storageId, nextRevs)

    setData(restoredData)
    pushHistory(restoredData)
    onSaveData(restoredData)
    setShowRevisionModal(false)
    showToast(`✓ Đã khôi phục thành công về dữ liệu của ${rev.revLabel}!`)
  }

  /* --- SELECTION & RANGE HELPERS --- */
  function isCellSelected(table: ActiveTableType, r: number, c: number): boolean {
    if (selection.table !== table) return false
    const minR = Math.min(selection.startRow, selection.endRow)
    const maxR = Math.max(selection.startRow, selection.endRow)
    const minC = Math.min(selection.startCol, selection.endCol)
    const maxC = Math.max(selection.startCol, selection.endCol)
    return r >= minR && r <= maxR && c >= minC && c <= maxC
  }

  function isRowSelected(table: ActiveTableType, r: number): boolean {
    if (selection.table !== table) return false
    const minR = Math.min(selection.startRow, selection.endRow)
    const maxR = Math.max(selection.startRow, selection.endRow)
    const minC = Math.min(selection.startCol, selection.endCol)
    const maxC = Math.max(selection.startCol, selection.endCol)
    const totalCols = table === 'wood' ? WOOD_COLS.length : HW_COLS.length
    return r >= minR && r <= maxR && minC === 0 && maxC >= totalCols - 1
  }

  function handleSelectRowHeader(table: ActiveTableType, r: number, shift: boolean) {
    const maxCol = (table === 'wood' ? WOOD_COLS.length : HW_COLS.length) - 1
    if (shift && selection.table === table) {
      setSelection((prev) => ({
        ...prev,
        endRow: r,
        endCol: maxCol,
      }))
    } else {
      setSelection({
        table,
        startRow: r,
        startCol: 0,
        endRow: r,
        endCol: maxCol,
        activeRow: r,
        activeCol: 0,
      })
    }
    setEditingCell(null)
  }

  function isCellActive(table: ActiveTableType, r: number, c: number): boolean {
    return selection.table === table && selection.activeRow === r && selection.activeCol === c
  }

  function handleMouseDownCell(table: ActiveTableType, r: number, c: number, shift: boolean) {
    if (shift && selection.table === table) {
      setSelection((prev) => ({
        ...prev,
        endRow: r,
        endCol: c,
      }))
    } else {
      setSelection({
        table,
        startRow: r,
        startCol: c,
        endRow: r,
        endCol: c,
        activeRow: r,
        activeCol: c,
      })
      setIsSelecting(true)
    }
    setEditingCell(null)
  }

  function handleMouseEnterCell(table: ActiveTableType, r: number, c: number) {
    if (isSelecting && selection.table === table) {
      setSelection((prev) => ({
        ...prev,
        endRow: r,
        endCol: c,
      }))
    }
  }

  useEffect(() => {
    function handleMouseUp() {
      setIsSelecting(false)
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  /* --- INLINE EDITING --- */
  function handleDoubleClickCell(table: ActiveTableType, r: number, c: number) {
    if (!isEditMode) {
      showToast('🔒 Bảng đang ở chế độ xem. Vui lòng bấm nút "✏️ Chỉnh sửa" ở trên để sửa ô!')
      return
    }
    const cols = table === 'wood' ? WOOD_COLS : HW_COLS
    if (cols[c]?.type === 'calc') return
    setEditingCell({ table, row: r, col: c })
  }

  useEffect(() => {
    if (editingCell && cellInputRef.current) {
      cellInputRef.current.focus()
      cellInputRef.current.select()
    }
  }, [editingCell])

  function handleCellChange(table: ActiveTableType, r: number, c: number, value: string) {
    const next = { ...data }
    if (table === 'wood') {
      const rows = [...next.wood]
      const col = WOOD_COLS[c]
      if (col && col.type !== 'calc') {
        const key = col.key as keyof BomSpreadsheetWoodRow
        rows[r] = {
          ...rows[r],
          [key]: col.type === 'number' ? (value === '' ? '' : parseFloat(value) || value) : value,
        }
      }
      next.wood = rows
    } else {
      const rows = [...next.hardware]
      const col = HW_COLS[c]
      if (col) {
        const key = col.key as keyof BomSpreadsheetHardwareRow
        rows[r] = {
          ...rows[r],
          [key]: col.type === 'number' ? (value === '' ? '' : parseFloat(value) || value) : value,
        }
      }
      next.hardware = rows
    }
    pushHistory(next)
  }

  /** Commit in-progress cell edit and close input so print/PDF never captures browser input chrome. */
  const commitEditingCellBeforePrint = useCallback(() => {
    const cell = editingCellRef.current
    if (!cell) return
    const input = cellInputRef.current
    flushSync(() => {
      if (input) {
        handleCellChange(cell.table, cell.row, cell.col, input.value)
      }
      setEditingCell(null)
    })
  }, [pushHistory])

  useEffect(() => {
    const onAfterPrint = () => setPrintLayout(null)
    window.addEventListener('beforeprint', commitEditingCellBeforePrint)
    window.addEventListener('afterprint', onAfterPrint)
    return () => {
      window.removeEventListener('beforeprint', commitEditingCellBeforePrint)
      window.removeEventListener('afterprint', onAfterPrint)
    }
  }, [commitEditingCellBeforePrint])

  /* --- COPY TO CLIPBOARD (TSV) --- */
  const copySelectionToClipboard = useCallback(() => {
    const { table, startRow, endRow, startCol, endCol } = selection
    const minR = Math.min(startRow, endRow)
    const maxR = Math.max(startRow, endRow)
    const minC = Math.min(startCol, endCol)
    const maxC = Math.max(startCol, endCol)

    const rows = table === 'wood' ? data.wood : data.hardware
    const cols = table === 'wood' ? WOOD_COLS : HW_COLS

    const lines: string[] = []
    for (let r = minR; r <= maxR && r < rows.length; r++) {
      const rowVals: string[] = []
      for (let c = minC; c <= maxC && c < cols.length; c++) {
        const col = cols[c]
        if (col.type === 'calc') {
          if (col.key === 'cbm') {
            const cbmVal = calculateWoodRowCbm(rows[r] as BomSpreadsheetWoodRow)
            rowVals.push(cbmVal > 0 ? cbmVal.toFixed(3) : '')
          } else if (col.key === 'qty_total') {
            const rawQtyPer =
              typeof (rows[r] as any).qty_per === 'number'
                ? (rows[r] as any).qty_per
                : parseFloat(String((rows[r] as any).qty_per || '0'))
            const effTotal = isNaN(rawQtyPer) ? 0 : rawQtyPer * orderQty
            rowVals.push(effTotal > 0 ? String(effTotal) : '')
          } else {
            rowVals.push('')
          }
        } else {
          const val = (rows[r] as any)[col.key]
          rowVals.push(val !== undefined && val !== null ? String(val) : '')
        }
      }
      lines.push(rowVals.join('\t'))
    }

    const tsv = lines.join('\n')
    navigator.clipboard.writeText(tsv).then(() => {
      showToast(`✓ Đã copy ${maxR - minR + 1} dòng × ${maxC - minC + 1} cột vào Clipboard (Dán vào Excel được ngay)`)
    })
  }, [selection, data, orderQty])

  /* --- PASTE FROM CLIPBOARD (TSV) --- */
  const applyTsvToActiveTable = useCallback((tsv: string) => {
    if (!tsv) return
    const { table, startRow, startCol } = selection
    const rawRows = tsv.split(/\r?\n/).map((l) => l.split('\t'))
    if (rawRows.length > 0 && rawRows[rawRows.length - 1].length === 1 && rawRows[rawRows.length - 1][0] === '') {
      rawRows.pop()
    }
    if (rawRows.length === 0) return

    const next = { ...data }
    const rows: any[] = table === 'wood' ? [...next.wood] : [...next.hardware]
    const cols = table === 'wood' ? WOOD_COLS : HW_COLS
    let addedCount = 0

    rawRows.forEach((rData, rOff) => {
      const targetR = startRow + rOff
      while (targetR >= rows.length) {
        if (table === 'wood') {
          rows.push(createBlankWoodRow())
        } else {
          rows.push(createBlankHardwareRow())
        }
        addedCount++
      }

      rData.forEach((cellVal, cOff) => {
        const targetC = startCol + cOff
        if (targetC < cols.length) {
          const col = cols[targetC]
          if (col.type !== 'calc') {
            const trimmed = cellVal.trim()
            if (col.type === 'number') {
              const num = parseFloat(trimmed.replace(/,/g, ''))
              ;(rows[targetR] as any)[col.key] = isNaN(num) ? (trimmed === '' ? '' : trimmed) : num
            } else {
              ;(rows[targetR] as any)[col.key] = trimmed
            }
          }
        }
      })
    })

    if (table === 'wood') {
      next.wood = rows as BomSpreadsheetWoodRow[]
    } else {
      next.hardware = rows as BomSpreadsheetHardwareRow[]
    }

    pushHistory(next)
    setSelection({
      table,
      startRow,
      startCol,
      endRow: startRow + rawRows.length - 1,
      endCol: Math.min(cols.length - 1, startCol + (rawRows[0] ? rawRows[0].length - 1 : 0)),
      activeRow: startRow,
      activeCol: startCol,
    })
    showToast(`✓ Đã paste ${rawRows.length} dòng × ${rawRows[0]?.length || 1} cột từ Excel!${addedCount > 0 ? ` (+${addedCount} dòng mới)` : ''}`)
  }, [selection, data, pushHistory])

  // Global paste shortcut Ctrl+V
  useEffect(() => {
    function handlePaste(ev: ClipboardEvent) {
      if (!isEditMode) return
      if (
        ev.target instanceof HTMLInputElement ||
        ev.target instanceof HTMLTextAreaElement
      ) {
        return
      }
      const text = ev.clipboardData?.getData('text/plain')
      if (text) {
        ev.preventDefault()
        applyTsvToActiveTable(text)
      }
    }
    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [isEditMode, applyTsvToActiveTable])

  // Global copy shortcut Ctrl+C
  useEffect(() => {
    function handleCopy(ev: KeyboardEvent) {
      if ((ev.ctrlKey || ev.metaKey) && ev.key === 'c') {
        if (
          ev.target instanceof HTMLInputElement ||
          ev.target instanceof HTMLTextAreaElement
        ) {
          return
        }
        copySelectionToClipboard()
      }
    }
    window.addEventListener('keydown', handleCopy)
    return () => window.removeEventListener('keydown', handleCopy)
  }, [copySelectionToClipboard])

  /* --- ROW MANAGEMENT --- */
  function addBlankWood() {
    if (!isEditMode) setIsEditMode(true)
    const newRow = createBlankWoodRow()
    const next = { ...data, wood: [...data.wood, newRow] }
    pushHistory(next)
    const newR = next.wood.length - 1
    setSelection({
      table: 'wood',
      startRow: newR,
      startCol: 0,
      endRow: newR,
      endCol: 0,
      activeRow: newR,
      activeCol: 0,
    })
    showToast('✓ Đã thêm 1 dòng GỖ ô trống hoàn toàn')
  }

  function addBlankHardware() {
    if (!isEditMode) setIsEditMode(true)
    const newRow = createBlankHardwareRow()
    const next = { ...data, hardware: [...data.hardware, newRow] }
    pushHistory(next)
    const newR = next.hardware.length - 1
    setSelection({
      table: 'hardware',
      startRow: newR,
      startCol: 0,
      endRow: newR,
      endCol: 0,
      activeRow: newR,
      activeCol: 0,
    })
    showToast('✓ Đã thêm 1 dòng NGŨ KIM ô trống hoàn toàn')
  }

  function deleteSingleRow(table: ActiveTableType, index: number) {
    if (!isEditMode) {
      showToast('🔒 Vui lòng bấm "✏️ Chỉnh sửa" trước khi xóa dòng!')
      return
    }
    const rows = table === 'wood' ? data.wood : data.hardware
    if (rows.length <= 1) {
      alert('Bảng cần giữ tối thiểu 1 dòng.')
      return
    }
    const next = { ...data }
    if (table === 'wood') {
      next.wood = next.wood.filter((_, i) => i !== index)
    } else {
      next.hardware = next.hardware.filter((_, i) => i !== index)
    }
    pushHistory(next)
    showToast('✓ Đã xóa 1 dòng')
  }

  function moveSingleRow(table: ActiveTableType, index: number, direction: 'up' | 'down') {
    if (!isEditMode) {
      showToast('🔒 Vui lòng bấm "✏️ Chỉnh sửa" trước khi di chuyển dòng!')
      return
    }
    const mover = direction === 'up' ? moveRowUp : moveRowDown
    const next = { ...data }
    if (table === 'wood') {
      next.wood = mover(next.wood, index)
    } else {
      next.hardware = mover(next.hardware, index)
    }
    pushHistory(next)
  }

  function deleteSelectedRows() {
    if (!isEditMode) {
      showToast('🔒 Vui lòng bấm "✏️ Chỉnh sửa" trước khi xóa dòng!')
      return
    }
    const { table, startRow, endRow } = selection
    const minR = Math.min(startRow, endRow)
    const maxR = Math.max(startRow, endRow)
    const count = maxR - minR + 1

    const next = { ...data }
    if (table === 'wood') {
      if (data.wood.length <= count) {
        next.wood = [createBlankWoodRow()]
      } else {
        next.wood = data.wood.filter((_, i) => i < minR || i > maxR)
      }
    } else {
      if (data.hardware.length <= count) {
        next.hardware = [createBlankHardwareRow()]
      } else {
        next.hardware = data.hardware.filter((_, i) => i < minR || i > maxR)
      }
    }

    pushHistory(next)
    setSelection((prev) => ({
      ...prev,
      startRow: Math.max(0, minR),
      endRow: Math.max(0, minR),
      activeRow: Math.max(0, minR),
    }))
    showToast(`✓ Đã xóa ${count} dòng`)
  }

  /* --- CBM CALCULATION & PILLS --- */
  const { totalCbm, cbmByWoodType } = useMemo(() => {
    const groups: Record<string, number> = {}
    let sum = 0
    data.wood.forEach((r) => {
      const cbm = calculateWoodRowCbm(r)
      sum += cbm
      const type = (r.wood_type_raw || '').trim()
      if (type) {
        groups[type] = (groups[type] || 0) + cbm
      }
    })
    return { totalCbm: sum, cbmByWoodType: groups }
  }, [data.wood])

  /* --- PRINT / PDF EXPORT (portrait A4 only) --- */
  function triggerPrint() {
    commitEditingCellBeforePrint()

    const existingStyle = document.getElementById('dynamic-print-orientation')
    if (existingStyle) existingStyle.remove()

    const style = document.createElement('style')
    style.id = 'dynamic-print-orientation'
    // Absolute A4 portrait dimensions — more reliable than "A4 portrait" keyword
    // for some browser/print-driver combos that remember a prior Landscape choice.
    style.innerHTML = `@page { size: 210mm 297mm; margin: 8mm; }`
    document.head.appendChild(style)

    flushSync(() => {
      setPrintLayout('portrait')
    })

    setTimeout(() => {
      window.print()
    }, 100)
  }

  /* --- EXCEL IMPORT & EXPORT --- */
  function handleExcelUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Always clear input so the same file can be chosen again.
    e.target.value = ''
    if (!file || !isEditMode) return
    try {
      assertWorkbookFileSize(file.size)
    } catch (err: any) {
      alert('Lỗi đọc file Excel: ' + err.message)
      return
    }

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const arrBuffer = evt.target?.result as ArrayBuffer
        const bytes = new Uint8Array(arrBuffer)
        validateWorkbookBytes(file.name, bytes)
        const workbook = readSafeWorkbook(bytes)
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
        const jsonRows: unknown[][] = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          raw: false,
          defval: '',
        })

        // Resolve columns by real header names (not fixed indices) to avoid
        // collisions like empty SL 1SP falling through to the Veneer column.
        const { wood: parsedWood, hardware: parsedHw } =
          parseBomSpreadsheetExcelRows(jsonRows)

        // Replace both tables outright — do not keep leftover rows from the old BOM.
        const next = applyParsedBomExcelImport(data, {
          wood: parsedWood,
          hardware: parsedHw,
        })
        pushHistory(next)
        showToast(`✓ Đã nạp ${parsedWood.length} chi tiết Gỗ & ${parsedHw.length} Ngũ kim từ Excel`)
      } catch (err: any) {
        alert('Lỗi đọc file Excel: ' + err.message)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  function handleExcelExport() {
    const colorStr = (activeConfig.colors || []).join(' - ')
    const woodStr = (activeConfig.woods || []).join(' - ')
    const asmStr = (activeConfig.assemblies || []).join(' - ')

    const excelRows = [
      ['BẢNG THÔNG TIN SẢN PHẨM & MÃ HÀNG'],
      ['MÃ HÀNG (SKU):', productCode, 'MÃ CẤU HÌNH:', activeConfig.code, 'TÊN SẢN PHẨM:', productName],
      [
        'KÍCH THƯỚC:',
        `${dimensionsMm.w} × ${dimensionsMm.d} × ${dimensionsMm.h} mm`,
        'KÍCH THƯỚC (INCH):',
        `${dimensionsInch.w}" × ${dimensionsInch.d}" × ${dimensionsInch.h}"`,
        'SỐ LƯỢNG:',
        orderQty,
      ],
      ['MÀU SƠN:', colorStr],
      ['GHI CHÚ MÀU SƠN:', colorNotes],
      ['LOẠI GỖ:', woodStr],
      ['KẾT CẤU:', asmStr, '', 'KHÁCH HÀNG:', customer],
      ['LẦN SỬA ĐỔI & NGÀY LẬP:', `${currentRev.revLabel} (${currentRev.savedAt})`, '', 'NGƯỜI LẬP BIỂU:', currentRev.author],
      [],
      ['ĐỊNH MỨC NGUYÊN VẬT LIỆU GỖ - ' + productCode],
      [
        'STT',
        'TÊN CHI TIẾT',
        'LOẠI GỖ',
        'DÀY',
        'RỘNG',
        'DÀI',
        'MỘNG',
        'SL 1SP',
        'SL TỔNG',
        'GHI CHÚ',
        'VENEER',
        'M3 TINH',
      ],
      ...data.wood.map((r, i) => {
        const rawQty = typeof r.qty_per === 'number' ? r.qty_per : parseFloat(String(r.qty_per || '0'))
        const effTotal = isNaN(rawQty) ? 0 : rawQty * orderQty
        return [
          i + 1,
          r.part_name,
          r.wood_type_raw,
          r.dims_finished_t,
          r.dims_finished_w,
          r.dims_finished_l,
          r.tenon,
          r.qty_per,
          effTotal > 0 ? effTotal : r.qty_total,
          r.notes,
          r.veneer_spec,
          calculateWoodRowCbm(r).toFixed(4),
        ]
      }),
      [],
      ['ĐỊNH MỨC NGUYÊN VẬT LIỆU NGŨ KIM - ' + productCode],
      ['STT', 'TÊN VẬT TƯ', 'QUY CÁCH', 'SL', 'SL TỔNG LÔ', 'ĐVT', 'MÀU', 'PHÂN LOẠI', 'GHI CHÚ'],
      ...data.hardware.map((r, i) => {
        const rawQty = typeof r.qty_per === 'number' ? r.qty_per : parseFloat(String(r.qty_per || '0'))
        const effTotal = isNaN(rawQty) ? 0 : rawQty * orderQty
        return [
          i + 1,
          r.name_raw,
          r.spec_raw,
          r.qty_per,
          effTotal > 0 ? effTotal : r.qty_total,
          r.uom_hint,
          r.color_hint,
          r.item_type,
          r.notes,
        ]
      }),
      [],
      ['KỸ THUẬT LẬP BIỂU: Hà Phan Thanh Tâm', '', '', 'BAN GIÁM ĐỐC DUYỆT: Nguyễn Thị Ngọc'],
    ]

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(excelRows)
    XLSX.utils.book_append_sheet(wb, ws, 'BOM Sản Xuất')
    XLSX.writeFile(wb, `BOM_${productCode}_${activeConfig.code || 'export'}.xlsx`)
    showToast('✓ Đã xuất file Excel kèm bảng thông tin mã hàng')
  }

  const colorStr = (activeConfig.colors || []).join(' - ') || '— (Chưa chọn màu)'
  const woodStr = (activeConfig.woods || []).join(' - ') || '— (Chưa chọn gỗ)'
  const asmStr = (activeConfig.assemblies || []).join(' - ') || '— (Chưa chọn kết cấu)'

  return (
    <div
      className={`bom-spreadsheet-root min-h-screen bg-slate-100/90 pb-16 font-sans text-xs text-slate-900${
        printLayout ? ` print-${printLayout}` : ''
      }`}
    >
      <style>{`
        /* A4 portrait absolute size — reinforces dynamic-print-orientation style. */
        @page { size: 210mm 297mm; margin: 8mm; }

        /* Screen & Print: wrap long-text cells (.bom-wrap-text / .table-cell-wrap). Numbers & codes stay nowrap. */
        .bom-spreadsheet-root .bom-wood-table td.bom-wrap-text,
        .bom-spreadsheet-root .bom-hw-table td.bom-wrap-text,
        .bom-spreadsheet-root td.table-cell-wrap,
        .bom-wrap-text {
          white-space: normal !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
          text-overflow: clip !important;
        }

        @media screen {
          .bom-print-only-tr {
            display: none !important;
          }
        }

        /* Print: fixed table layout; wrap long-text cells. Proportional column widths */
        @media print {
          .bom-screen-only-tr {
            display: none !important;
          }
          .bom-print-only-tr {
            display: table-row !important;
          }
          .bom-spreadsheet-root thead {
            display: table-header-group !important;
          }
          .bom-spreadsheet-root tr {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .bom-spreadsheet-root section {
            border: none !important;
            box-shadow: none !important;
            margin-bottom: 12px !important;
          }
          .bom-spreadsheet-root .product-info-table td {
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: anywhere !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }
          .bom-spreadsheet-root table,
          .bom-spreadsheet-root th,
          .bom-spreadsheet-root td,
          .bom-spreadsheet-root .product-info-table,
          .bom-spreadsheet-root .product-info-table th,
          .bom-spreadsheet-root .product-info-table td,
          .bom-spreadsheet-root .bom-wood-table,
          .bom-spreadsheet-root .bom-wood-table th,
          .bom-spreadsheet-root .bom-wood-table td,
          .bom-spreadsheet-root .bom-hw-table,
          .bom-spreadsheet-root .bom-hw-table th,
          .bom-spreadsheet-root .bom-hw-table td {
            border: 1px solid #000 !important;
            border-radius: 0 !important;
            border-collapse: collapse !important;
          }
          * {
            border-radius: 0 !important;
          }
          .bom-spreadsheet-root .bom-wood-table,
          .bom-spreadsheet-root .bom-hw-table {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          /* Print widths belong to colgroup, so hidden screen-only columns do not shift the leaf grid. */
          .bom-wood-table col.col-wood-stt { width: 3.5% !important; }
          .bom-wood-table col.col-wood-part { width: 20% !important; }
          .bom-wood-table col.col-wood-type { width: 10% !important; }
          .bom-wood-table col.col-wood-t { width: 4.5% !important; }
          .bom-wood-table col.col-wood-w { width: 4.5% !important; }
          .bom-wood-table col.col-wood-l { width: 5.5% !important; }
          .bom-wood-table col.col-wood-tenon { width: 8% !important; }
          .bom-wood-table col.col-wood-qty-per { width: 4.5% !important; }
          .bom-wood-table col.col-wood-notes { width: 21.5% !important; }
          .bom-wood-table col.col-wood-veneer { width: 12.5% !important; }
          .bom-wood-table col.col-wood-cbm { width: 6% !important; }
          .bom-hw-table col.col-hw-stt { width: 4.5% !important; }
          .bom-hw-table col.col-hw-name { width: 26% !important; }
          .bom-hw-table col.col-hw-spec { width: 20% !important; }
          .bom-hw-table col.col-hw-qty { width: 6.5% !important; }
          .bom-hw-table col.col-hw-uom { width: 6.5% !important; }
          .bom-hw-table col.col-hw-color { width: 10.5% !important; }
          .bom-hw-table col.col-hw-type { width: 10% !important; }
          .bom-hw-table col.col-hw-notes { width: 16% !important; }
          .bom-spreadsheet-root .bom-wood-table th,
          .bom-spreadsheet-root .bom-wood-table td,
          .bom-spreadsheet-root .bom-hw-table th,
          .bom-spreadsheet-root .bom-hw-table td {
            box-sizing: border-box !important;
            overflow: visible !important;
            text-overflow: clip !important;
            padding: 2.5px 3.5px !important;
            font-size: 11px !important;
            line-height: 1.32 !important;
            font-weight: normal !important;
            vertical-align: top;
          }
          .bom-spreadsheet-root thead th {
            font-weight: bold !important;
            text-align: center !important;
            background-color: #f1f5f9 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* --- WOOD TABLE COLUMN WIDTHS (A4 Portrait 100%) --- */
          .bom-wood-table .col-wood-stt { width: 3.5% !important; }
          .bom-wood-table .col-wood-part { width: 20% !important; }
          .bom-wood-table .col-wood-type { width: 10% !important; }
          .bom-wood-table .col-wood-t { width: 4.5% !important; }
          .bom-wood-table .col-wood-w { width: 4.5% !important; }
          .bom-wood-table .col-wood-l { width: 5.5% !important; }
          .bom-wood-table .col-wood-tenon { width: 8% !important; }
          .bom-wood-table .col-wood-qty-per { width: 4.5% !important; }
          .bom-wood-table .col-wood-notes { width: 21.5% !important; }
          .bom-wood-table .col-wood-veneer { width: 12.5% !important; }
          .bom-wood-table .col-wood-cbm { width: 6% !important; }

          /* Group headers */
          .bom-wood-table .col-wood-dims-group { width: 22.5% !important; }
          .bom-wood-table .col-wood-qty-group { width: 4.5% !important; }

          /* --- HARDWARE TABLE COLUMN WIDTHS (A4 Portrait 100%) --- */
          .bom-hw-table .col-hw-stt { width: 4.5% !important; }
          .bom-hw-table .col-hw-name { width: 26% !important; }
          .bom-hw-table .col-hw-spec { width: 20% !important; }
          .bom-hw-table .col-hw-qty { width: 6.5% !important; }
          .bom-hw-table .col-hw-uom { width: 6.5% !important; }
          .bom-hw-table .col-hw-color { width: 10.5% !important; }
          .bom-hw-table .col-hw-type { width: 10% !important; }
          .bom-hw-table .col-hw-notes { width: 16% !important; }

          /* Group headers */
          .bom-hw-table .col-hw-qty-group { width: 6.5% !important; }

          /* Short columns: STT, dimensions, Qty, CBM stay nowrap */
          .bom-wood-table .col-wood-stt,
          .bom-wood-table .col-wood-t,
          .bom-wood-table .col-wood-w,
          .bom-wood-table .col-wood-l,
          .bom-wood-table .col-wood-qty-per,
          .bom-wood-table .col-wood-cbm,
          .bom-hw-table .col-hw-stt,
          .bom-hw-table .col-hw-qty,
          .bom-hw-table .col-hw-uom {
            white-space: nowrap !important;
            text-align: center !important;
          }
          .bom-wood-table .col-wood-cbm {
            text-align: right !important;
          }

          /* Long text cells: wrap cleanly */
          .bom-spreadsheet-root .bom-wood-table td.bom-wrap-text,
          .bom-spreadsheet-root .bom-hw-table td.bom-wrap-text,
          .bom-spreadsheet-root td.table-cell-wrap {
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: anywhere !important;
          }
          /* print-portrait: no further font shrink */
          /* Wood-type screen pills → plain cell text when printing */
          .bom-spreadsheet-root .bom-wood-type-badge {
            background: transparent !important;
            color: inherit !important;
            font-weight: inherit !important;
            font-size: inherit !important;
            padding: 0 !important;
            border-radius: 0 !important;
            border: none !important;
          }
          .bom-spreadsheet-root .bom-wood-table tbody tr,
          .bom-spreadsheet-root .bom-hw-table tbody tr {
            color: inherit !important;
            font-weight: normal !important;
          }
          /* Hide selection chrome only — do not clear React selection state. */
          .bom-spreadsheet-root td.ring-1,
          .bom-spreadsheet-root td.ring-2 {
            --tw-ring-shadow: 0 0 #0000 !important;
            box-shadow: none !important;
            outline: none !important;
          }
          .bom-spreadsheet-root td.bg-blue-100,
          .bom-spreadsheet-root td.bg-blue-50 {
            background-color: transparent !important;
          }
          .bom-spreadsheet-root td.bg-blue-600 {
            background-color: #f8fafc !important;
            color: #334155 !important;
          }
        }
      `}</style>
      <SafeBlocker when={hasUnsavedChanges} />
      {/* ---------------- SPREADSHEET HEADER BAR ---------------- */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2.5 text-white shadow-md print:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white border border-slate-700 transition hover:bg-slate-700 active:scale-95"
          >
            <ArrowLeft className="h-4 w-4" />
            Quay lại Chi tiết Mã hàng {productCode}
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white">
              BOM Sản Xuất — Mã hàng: {productCode}
            </span>
            <span className="text-[11px] text-slate-300">
              Màu: <strong className="text-rose-300">{colorStr}</strong> | Gỗ:{' '}
              <strong className="text-amber-300">{woodStr}</strong> | Kết cấu:{' '}
              <strong className="text-blue-300">{asmStr}</strong> | Trạng thái:{' '}
              <span
                className={`font-semibold ${
                  activeConfig.status === 'OK' ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {activeConfig.status}
              </span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Nút sửa / lưu */}
          <button
            type="button"
            onClick={handleToggleEdit}
            className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold transition shadow-xs active:scale-95 ${
              isEditMode
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white border border-blue-500'
            }`}
          >
            {isEditMode ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {isEditMode ? <><Save size={15} aria-hidden="true" /> Lưu</> : <><Pencil size={15} aria-hidden="true" /> Chỉnh sửa</>}
          </button>

          {/* Nút Lịch sử phiên bản */}
          <button
            type="button"
            onClick={() => setShowRevisionModal(true)}
            className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-white border border-slate-700 hover:bg-slate-700 transition"
            title="Xem lịch sử các phiên bản sửa đổi định mức"
          >
            <History className="h-3.5 w-3.5 text-sky-400" />
            Lịch sử ({currentRev.revLabel})
          </button>

          {/* Nút So sánh cấu hình */}
          <button
            type="button"
            onClick={() => setShowCompareModal(true)}
            className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-white border border-slate-700 hover:bg-slate-700 transition"
            title="So sánh khác biệt giữa các cấu hình BOM của sản phẩm"
          >
            <GitCompare className="h-3.5 w-3.5 text-amber-400" />
            So sánh ({allConfigs.length})
          </button>

          {/* Import Excel — chỉ khi đang sửa (giống lock ô) */}
          {isEditMode && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-white border border-slate-700 hover:bg-slate-700"
                title="Nhập bảng từ file Excel (.xlsx, .xlsm)"
              >
                <Upload className="h-3.5 w-3.5" />
                Nạp Excel
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="hidden"
                onChange={handleExcelUpload}
              />
            </>
          )}

          {/* Export Excel */}
          <button
            type="button"
            onClick={handleExcelExport}
            className="inline-flex items-center gap-1 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-white border border-slate-700 hover:bg-slate-700"
            title="Xuất bảng BOM ra file Excel"
          >
            <Download className="h-3.5 w-3.5" />
            Xuất Excel
          </button>

          {/* In BOM / Xuất PDF A4 — portrait only (no landscape menu) */}
          <button
            type="button"
            onClick={() => triggerPrint()}
            className="inline-flex items-center gap-1.5 rounded bg-red-700 px-3 py-1.5 text-xs font-bold text-white border border-red-600 hover:bg-red-800 shadow-xs"
            title="In hoặc lưu file PDF khổ A4 Đứng"
          >
            <Printer className="h-3.5 w-3.5" />
            In BOM / Xuất PDF (A4 Đứng)
          </button>
        </div>
      </header>

      {/* Main container */}
      <main className="mx-auto max-w-[1480px] p-4">
        {/* ---------------- TIÊU ĐỀ IN CHÍNH THỨC (CHỈ HIỆN KHI IN) ---------------- */}
        <div className="hidden print:block border-b-2 border-slate-900 pb-3 mb-3">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
                <img
                  src="/logo-mocphat.jpg"
                  alt="Mộc Phát"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-sm font-black uppercase text-slate-900">
                  CÔNG TY TNHH SẢN XUẤT & THƯƠNG MẠI MỘC PHÁT
                </h1>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-700 leading-tight">
              <div>
                Phiên bản: <strong>{currentRev.revLabel} ({currentRev.savedAt})</strong>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- BẢNG THÔNG TIN MÃ HÀNG TRÊN CÙNG ---------------- */}
        <section className="mb-4 rounded-none border border-slate-300 bg-white shadow-none overflow-hidden print:shadow-none print:border-black print:rounded-none">
          <div className="flex items-center justify-between bg-slate-900 px-3.5 py-2 text-white print:bg-slate-900 print:text-white">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
              <span className="h-2 w-2 rounded-full bg-sky-400"></span>
              <span>BẢNG THÔNG TIN SẢN PHẨM & MÃ HÀNG</span>
            </div>
            <div className="text-xs text-slate-300">
              Cấu hình BOM:{' '}
              <span className="rounded bg-sky-500/20 px-2 py-0.5 font-mono font-bold text-sky-300 border border-sky-400/30">
                {productCode}
              </span>
            </div>
          </div>

          <div className="flex items-stretch justify-between p-3 gap-3 bg-white">
            <div className="flex-1 overflow-x-auto">
              <table className="product-info-table w-full text-left text-xs border-collapse">
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="w-24 bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      MÃ HÀNG (SKU)
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-mono text-base font-black text-slate-900">
                      {productCode}
                    </td>
                    <td className="w-28 bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      TÊN SẢN PHẨM
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-bold text-slate-900">
                      {productName}
                    </td>
                    <td className="w-24 bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      KHÁCH HÀNG
                    </td>
                    <td className="px-2.5 py-1.5 font-semibold text-slate-800">
                      {customer}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      KÍCH THƯỚC
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-semibold text-blue-700 whitespace-nowrap">
                      {dimensionsMm.w} × {dimensionsMm.d} × {dimensionsMm.h} mm
                    </td>
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      KÍCH THƯỚC (INCH)
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-semibold text-emerald-700 whitespace-nowrap">
                      {dimensionsInch.w}&quot; × {dimensionsInch.d}&quot; × {dimensionsInch.h}&quot;
                    </td>
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      LOẠI GỖ
                    </td>
                    <td className="px-2.5 py-1.5 font-bold text-amber-900">
                      {woodStr}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      MÀU SƠN
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-bold text-rose-800">
                      {colorStr}{colorNotes ? ` (${colorNotes})` : ''}
                    </td>
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      KẾT CẤU
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 font-bold text-blue-800">
                      {asmStr}
                    </td>
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      SỐ LƯỢNG
                    </td>
                    <td className="px-2.5 py-1.5 font-semibold text-slate-800">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={orderQty}
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value, 10) || 1)
                          setOrderQty(val)
                        }}
                        className="w-16 rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-xs font-bold text-slate-900 focus:border-blue-500 focus:outline-none print:hidden"
                        title="Số lượng đơn hàng"
                      />
                      <span className="hidden print:inline font-mono font-bold">{orderQty}</span>
                    </td>
                  </tr>

                  <tr>
                    <td className="bg-slate-50 px-2.5 py-1.5 font-bold text-slate-600 uppercase border-r border-slate-200 whitespace-nowrap">
                      LẦN SỬA ĐỔI &amp; NGÀY LẬP
                    </td>
                    <td colSpan={5} className="px-2.5 py-1.5 text-slate-600">
                      <div className="flex items-center justify-between">
                        <span>
                          <strong className="text-slate-900 font-bold">{currentRev.revLabel}</strong> ({currentRev.savedAt})
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowRevisionModal(true)}
                          className="inline-flex items-center gap-1 rounded bg-slate-100 hover:bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-700 border border-slate-300 transition print:hidden"
                          title="Xem toàn bộ lịch sử các lần sửa đổi và so sánh chi tiết"
                        >
                          <History className="h-3 w-3 text-sky-600" />
                          Lịch sử ({revisions.length})
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Product Image Box (~35mm, solid black border, rounded-none) */}
            <div
              className="product-photo-box w-[35mm] h-[35mm] min-w-[35mm] min-h-[35mm] max-w-[35mm] max-h-[35mm] border border-black rounded-none flex items-center justify-center p-0.5 bg-white overflow-hidden text-center shrink-0 self-center"
              style={{ width: '35mm', height: '35mm', border: '1px solid #000', borderRadius: '0' }}
            >
              {productImageUrl ? (
                <img
                  src={productImageUrl}
                  alt={productName}
                  className="w-full h-full object-contain"
                  style={{ borderRadius: '0' }}
                />
              ) : null}
            </div>
          </div>
        </section>

        {/* ---------------- THANH CÔNG CỤ EXCEL GRID ---------------- */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-300 bg-white p-2 shadow-xs print:hidden">
          <button
            type="button"
            onClick={copySelectionToClipboard}
            className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800 border border-slate-300 transition hover:bg-slate-200"
            title="Sao chép vùng chọn sang clipboard để dán vào Excel (Ctrl+C)"
          >
            <Copy className="h-3.5 w-3.5 text-slate-600" />
            Copy vùng chọn (Ctrl+C)
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!isEditMode) {
                showToast('🔒 Vui lòng bấm "✏️ Chỉnh sửa" trước khi dán từ Excel!')
                return
              }
              try {
                const text = await navigator.clipboard.readText()
                if (text) applyTsvToActiveTable(text)
              } catch {
                const text = prompt('Dán (Ctrl+V) dữ liệu sao chép từ Excel vào đây:')
                if (text) applyTsvToActiveTable(text)
              }
            }}
            className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800 border border-slate-300 transition hover:bg-slate-200"
            title="Dán dữ liệu từ Excel vào bảng (Ctrl+V)"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-slate-600" />
            Paste từ Excel (Ctrl+V)
          </button>

          <div className="h-4 w-px bg-slate-300 mx-1"></div>

          {/* Rule: "Khi tạo mới mặc định trống (trong ô ko có text)" */}
          <button
            type="button"
            onClick={addBlankWood}
            className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
            title="Thêm 1 dòng phôi gỗ mới (ô trống hoàn toàn)"
          >
            <Plus className="h-3.5 w-3.5" />
            + Thêm dòng Gỗ
          </button>
          <button
            type="button"
            onClick={addBlankHardware}
            className="inline-flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1.5 text-xs font-bold text-blue-800 border border-blue-300 hover:bg-blue-100"
            title="Thêm 1 dòng ngũ kim mới (ô trống hoàn toàn)"
          >
            <Plus className="h-3.5 w-3.5" />
            + Thêm dòng Ngũ kim
          </button>
          <button
            type="button"
            onClick={deleteSelectedRows}
            className="inline-flex items-center gap-1 rounded bg-red-50 px-2.5 py-1.5 text-xs font-bold text-red-700 border border-red-200 hover:bg-red-100"
            title="Xóa các dòng đang chọn"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Xóa dòng chọn
          </button>

          <div className="h-4 w-px bg-slate-300 mx-1"></div>

          <button
            type="button"
            disabled={history.length === 0}
            onClick={handleUndo}
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 border border-slate-300 disabled:opacity-40 hover:bg-slate-200"
            title="Hoàn tác (Undo)"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </button>
          <button
            type="button"
            disabled={redoList.length === 0}
            onClick={handleRedo}
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 border border-slate-300 disabled:opacity-40 hover:bg-slate-200"
            title="Làm lại (Redo)"
          >
            <Redo2 className="h-3.5 w-3.5" />
            Redo
          </button>

          <div className="ml-auto text-[11px] text-slate-500 font-medium">
            Ô đang chọn:{' '}
            <strong className="text-slate-800">
              {selection.table === 'wood' ? 'Gỗ' : 'Ngũ kim'} [Dòng{' '}
              {Math.min(selection.startRow, selection.endRow) + 1} -{' '}
              {Math.max(selection.startRow, selection.endRow) + 1}]
            </strong>
          </div>
        </div>

        {/* ---------------- BẢNG 1: ĐỊNH MỨC NGUYÊN VẬT LIỆU GỖ ---------------- */}
        <section className="mb-5 rounded-lg border border-slate-300 bg-white shadow-xs overflow-hidden print:shadow-none print:border-slate-800">
          <div className="flex items-center justify-between bg-slate-900 px-3.5 py-2 text-white print:bg-slate-900 print:text-white">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
              <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
              <span>ĐỊNH MỨC NGUYÊN VẬT LIỆU GỖ - {productCode}</span>
            </div>
            <button
              type="button"
              onClick={addBlankWood}
              className="inline-flex items-center gap-1 rounded bg-emerald-700 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-emerald-600 print:hidden"
            >
              <Plus className="h-3 w-3" />
              Thêm dòng
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="bom-wood-table w-full border-collapse text-left text-xs table-fixed">
              <colgroup>
                <col className="col-wood-stt" />
                <col className="col-wood-part" />
                <col className="col-wood-type" />
                <col className="col-wood-t" />
                <col className="col-wood-w" />
                <col className="col-wood-l" />
                <col className="col-wood-tenon" />
                <col className="col-wood-qty-per" />
                <col className="print:hidden" />
                <col className="col-wood-notes" />
                <col className="col-wood-veneer" />
                <col className="col-wood-cbm" />
                <col className="print:hidden" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-300 bg-slate-100 text-center font-bold text-slate-700 text-[11px] uppercase">
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '42px' }}>
                    STT
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2" style={{ width: '260px' }}>
                    TÊN CHI TIẾT SẢN PHẨM
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '100px' }}>
                    LOẠI GỖ / PHÔI
                  </th>
                  <th colSpan={4} className="border-r border-slate-300 px-2 py-1 bg-slate-200/70">
                    KÍCH THƯỚC TINH (MM)
                  </th>
                  {/* colSpan 1 when printing: qty_total is print:hidden */}
                  <th
                    colSpan={printLayout || isPrintMedia ? 1 : 2}
                    className="border-r border-slate-300 px-2 py-1 bg-slate-200/70"
                  >
                    SỐ LƯỢNG
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2" style={{ width: '140px' }}>
                    GHI CHÚ
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2" style={{ width: '120px' }}>
                    VENEER
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '80px' }}>
                    M³
                  </th>
                  <th rowSpan={2} className="px-2 py-2 print:hidden" style={{ width: '72px' }}>
                    XÓA
                  </th>
                </tr>
                <tr className="border-b border-slate-300 bg-slate-100 text-center font-bold text-slate-700 text-[10px] uppercase">
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '42px' }}>
                    Dày
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '42px' }}>
                    Rộng
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '42px' }}>
                    Dài
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '70px' }}>
                    Mộng
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '45px' }}>
                    SL 1SP
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1 print:hidden" style={{ width: '60px' }}>
                    SL Tổng
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.wood.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="py-8 text-center text-xs text-slate-400">
                      Chưa có chi tiết phôi gỗ nào. Bấm "+ Thêm dòng Gỗ" hoặc dán từ Excel để thêm chi tiết.
                    </td>
                  </tr>
                ) : (
                  data.wood.map((r, rIdx) => {
                  const cbm = calculateWoodRowCbm(r)
                  const woodLower = String(r.wood_type_raw || '').toLowerCase()
                  const isMdf = woodLower.includes('mdf')
                  const isPlywood = woodLower.includes('plywood')

                  return (
                    <tr
                      key={r.id || rIdx}
                      className={`hover:bg-slate-50 transition-colors ${
                        isMdf ? 'text-pink-600 font-semibold' : isPlywood ? 'text-blue-600 font-semibold' : ''
                      }`}
                    >
                      <td
                        onClick={(e) => handleSelectRowHeader('wood', rIdx, e.shiftKey)}
                        className={`border-r border-slate-200 text-center font-mono font-bold py-1.5 select-none cursor-pointer transition ${
                          isRowSelected('wood', rIdx)
                            ? `bg-blue-600 text-white font-black shadow-xs ${PRINT_STT_ROW_SELECTED}`
                            : 'bg-slate-50 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
                        }`}
                        title="Bấm vào STT để chọn toàn bộ hàng này (giống Excel)"
                      >
                        {rIdx < 9 ? `0${rIdx + 1}` : rIdx + 1}
                      </td>

                      {WOOD_COLS.map((col, cIdx) => {
                        const selected = isCellSelected('wood', rIdx, cIdx)
                        const active = isCellActive('wood', rIdx, cIdx)
                        const isEditingThis =
                          editingCell?.table === 'wood' &&
                          editingCell.row === rIdx &&
                          editingCell.col === cIdx

                        let cellContent: React.ReactNode = (r as any)[col.key] || '—'
                        if (col.type === 'calc') {
                          if (col.key === 'cbm') {
                            cellContent = cbm > 0 ? (
                              <span className="font-mono font-bold text-slate-900">
                                {cbm.toFixed(3)}
                              </span>
                            ) : (
                              '—'
                            )
                          } else if (col.key === 'qty_total') {
                            const rawQtyPer =
                              typeof r.qty_per === 'number'
                                ? r.qty_per
                                : parseFloat(String(r.qty_per || '0'))
                            const effectiveTotal = isNaN(rawQtyPer) ? 0 : rawQtyPer * orderQty
                            cellContent = (
                              <span className="font-mono font-bold text-slate-900">
                                {effectiveTotal > 0
                                  ? Number.isInteger(effectiveTotal)
                                    ? effectiveTotal
                                    : effectiveTotal.toFixed(2)
                                  : '—'}
                              </span>
                            )
                          }
                        } else if (col.key === 'wood_type_raw' && r.wood_type_raw) {
                          cellContent = (
                            <span
                              className={`bom-wood-type-badge rounded px-1.5 py-0.5 text-[11px] font-bold ${
                                isMdf
                                  ? 'bg-pink-100 text-pink-700'
                                  : isPlywood
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {r.wood_type_raw}
                            </span>
                          )
                        }

                        return (
                          <td
                            key={col.key}
                            title={isEditingThis ? undefined : String((r as any)[col.key] || '')}
                            onMouseDown={(e) => handleMouseDownCell('wood', rIdx, cIdx, e.shiftKey)}
                            onMouseEnter={() => handleMouseEnterCell('wood', rIdx, cIdx)}
                            onDoubleClick={() => handleDoubleClickCell('wood', rIdx, cIdx)}
                            className={`border-r border-slate-200 px-2 py-1.5 select-none cursor-cell overflow-hidden text-ellipsis whitespace-nowrap ${
                              WOOD_COL_PRINT_CLASSES[col.key] || ''
                            } ${
                              col.key === 'part_name' || col.key === 'notes' || col.key === 'veneer_spec' || col.key === 'wood_type_raw' || col.key === 'tenon'
                                ? 'bom-wrap-text text-left'
                                : col.type === 'calc'
                                  ? 'text-right'
                                  : 'text-center'
                            } ${
                              active
                                ? `bg-blue-100 ring-2 ring-blue-600 ring-inset z-10 ${PRINT_CELL_SELECTION_RING}`
                                : selected
                                  ? `bg-blue-50 ring-1 ring-blue-400 ring-inset ${PRINT_CELL_SELECTION_RING}`
                                  : ''
                            }`}
                          >
                            {isEditingThis ? (
                              <input
                                ref={cellInputRef}
                                type={col.type === 'number' ? 'number' : 'text'}
                                defaultValue={(r as any)[col.key] || ''}
                                onBlur={(e) => {
                                  handleCellChange('wood', rIdx, cIdx, e.target.value)
                                  setEditingCell(null)
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellChange('wood', rIdx, cIdx, (e.target as any).value)
                                    setEditingCell(null)
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null)
                                  }
                                }}
                                className="w-full rounded border border-blue-500 bg-white px-1 py-0.5 text-xs outline-none"
                              />
                            ) : (
                              cellContent
                            )}
                          </td>
                        )
                      })}

                      <td className="text-center py-1 px-1 print:hidden whitespace-nowrap">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveSingleRow('wood', rIdx, 'up')}
                            disabled={rIdx === 0}
                            className={`rounded p-1 transition ${
                              rIdx === 0
                                ? 'opacity-30 cursor-not-allowed text-slate-400'
                                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                            }`}
                            title="Di chuyển dòng lên"
                            aria-label="Di chuyển dòng lên"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSingleRow('wood', rIdx, 'down')}
                            disabled={rIdx === data.wood.length - 1}
                            className={`rounded p-1 transition ${
                              rIdx === data.wood.length - 1
                                ? 'opacity-30 cursor-not-allowed text-slate-400'
                                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                            }`}
                            title="Di chuyển dòng xuống"
                            aria-label="Di chuyển dòng xuống"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSingleRow('wood', rIdx)}
                            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 transition"
                            title="Xóa dòng này"
                            aria-label="Xóa dòng này"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
              </tbody>
            </table>
          </div>

          {/* Thanh tổng khối theo từng loại gỗ (Yellow Bar) */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t-2 border-amber-300 bg-amber-100 px-4 py-2 text-amber-950 font-bold print:bg-amber-100 print:border-amber-400">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-amber-900">
                TỔNG SỐ KHỐI THEO TỪNG LOẠI GỖ:
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {Object.entries(cbmByWoodType).map(([type, val]) => (
                  <div
                    key={type}
                    className="flex items-center gap-1 rounded bg-white px-2 py-0.5 text-xs border border-amber-300 shadow-2xs"
                  >
                    <span className="text-slate-700">{type}:</span>
                    <span className="font-mono text-amber-900 font-black">{val.toFixed(3)} m³</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-amber-900">
                TỔNG CỘNG SỐ KHỐI (CBM):
              </span>
              <span className="rounded bg-amber-900 px-3 py-1 font-mono text-sm font-black text-amber-100 shadow-2xs">
                {totalCbm.toFixed(3)} m³
              </span>
            </div>
          </div>
        </section>

        {/* ---------------- BẢNG 2: ĐỊNH MỨC NGUYÊN VẬT LIỆU NGŨ KIM ---------------- */}
        <section className="mb-5 rounded-lg border border-slate-300 bg-white shadow-xs overflow-hidden print:shadow-none print:border-slate-800 print:break-inside-avoid">
          <div className="flex items-center justify-between bg-slate-900 px-3.5 py-2 text-white print:bg-slate-900 print:text-white">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
              <span className="h-2 w-2 rounded-full bg-sky-400"></span>
              <span>ĐỊNH MỨC NGUYÊN VẬT LIỆU NGŨ KIM - {productCode}</span>
            </div>
            <button
              type="button"
              onClick={addBlankHardware}
              className="inline-flex items-center gap-1 rounded bg-blue-700 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-blue-600 print:hidden"
            >
              <Plus className="h-3 w-3" />
              Thêm dòng
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="bom-hw-table w-full border-collapse text-left text-xs table-fixed">
              <colgroup>
                <col className="col-hw-stt" />
                <col className="col-hw-name" />
                <col className="col-hw-spec" />
                <col className="col-hw-qty" />
                <col className="print:hidden" />
                <col className="col-hw-uom" />
                <col className="col-hw-color" />
                <col className="col-hw-type" />
                <col className="col-hw-notes" />
                <col className="print:hidden" />
                <col className="print:hidden" />
                <col className="print:hidden" />
                <col className="print:hidden" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-300 bg-slate-100 text-center font-bold text-slate-700 text-[11px] uppercase">
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '42px' }}>
                    STT
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2 text-left" style={{ width: '240px' }}>
                    TÊN VẬT TƯ
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2 text-left" style={{ width: '160px' }}>
                    QUY CÁCH
                  </th>
                  {/* colSpan 1 when printing: qty_total is print:hidden */}
                  <th
                    colSpan={printLayout || isPrintMedia ? 1 : 2}
                    className="border-r border-slate-300 px-2 py-1 bg-slate-200/70"
                  >
                    SỐ LƯỢNG
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '60px' }}>
                    ĐƠN VỊ
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '100px' }}>
                    MÀU SẮC
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2" style={{ width: '70px' }}>
                    PHÂN LOẠI
                  </th>
                  <th rowSpan={2} className="bom-wrap-text border-r border-slate-300 px-2 py-2 text-left" style={{ width: '170px' }}>
                    GHI CHÚ
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2 text-left print:hidden no-print" style={{ width: '180px' }}>
                    VẬT TƯ ÁNH XẠ
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2 text-right print:hidden no-print" style={{ width: '100px' }}>
                    ĐƠN GIÁ
                  </th>
                  <th rowSpan={2} className="border-r border-slate-300 px-2 py-2 text-right print:hidden no-print" style={{ width: '110px' }}>
                    THÀNH TIỀN
                  </th>
                  <th rowSpan={2} className="px-2 py-2 print:hidden no-print" style={{ width: '72px' }}>
                    XÓA
                  </th>
                </tr>
                <tr className="border-b border-slate-300 bg-slate-100 text-center font-bold text-slate-700 text-[10px] uppercase">
                  <th className="border-r border-slate-300 px-1 py-1" style={{ width: '50px' }}>
                    SL
                  </th>
                  <th className="border-r border-slate-300 px-1 py-1 print:hidden" style={{ width: '65px' }}>
                    SL Tổng Lô
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.hardware.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-xs text-slate-400">
                      Chưa có vật tư ngũ kim nào. Bấm "+ Thêm dòng Ngũ kim" hoặc dán từ Excel để thêm vật tư.
                    </td>
                  </tr>
                ) : (
                  data.hardware.map((r, rIdx) => (
                  <tr key={r.id || rIdx} className="hover:bg-slate-50 transition-colors">
                    <td
                      onClick={(e) => handleSelectRowHeader('hardware', rIdx, e.shiftKey)}
                      className={`border-r border-slate-200 text-center font-mono font-bold py-1.5 select-none cursor-pointer transition ${
                        isRowSelected('hardware', rIdx)
                          ? `bg-blue-600 text-white font-black shadow-xs ${PRINT_STT_ROW_SELECTED}`
                          : 'bg-slate-50 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
                      }`}
                      title="Bấm vào STT để chọn toàn bộ hàng này (giống Excel)"
                    >
                      {rIdx < 9 ? `0${rIdx + 1}` : rIdx + 1}
                    </td>

                    {HW_COLS.map((col, cIdx) => {
                      const selected = isCellSelected('hardware', rIdx, cIdx)
                      const active = isCellActive('hardware', rIdx, cIdx)
                      const isEditingThis =
                        editingCell?.table === 'hardware' &&
                        editingCell.row === rIdx &&
                        editingCell.col === cIdx

                      let cellContent: React.ReactNode = (r as any)[col.key] || '—'
                      if (col.key === 'qty_total') {
                        const rawQtyPer =
                          typeof r.qty_per === 'number'
                            ? r.qty_per
                            : parseFloat(String(r.qty_per || '0'))
                        const effectiveTotal = isNaN(rawQtyPer) ? 0 : rawQtyPer * orderQty
                        cellContent = (
                          <span className="font-mono font-bold text-slate-900">
                            {effectiveTotal > 0
                              ? Number.isInteger(effectiveTotal)
                                ? effectiveTotal
                                : effectiveTotal.toFixed(2)
                              : '—'}
                          </span>
                        )
                      }

                      return (
                        <td
                          key={col.key}
                          title={isEditingThis ? undefined : String((r as any)[col.key] || '')}
                          onMouseDown={(e) =>
                            handleMouseDownCell('hardware', rIdx, cIdx, e.shiftKey)
                          }
                          onMouseEnter={() => handleMouseEnterCell('hardware', rIdx, cIdx)}
                          onDoubleClick={() => handleDoubleClickCell('hardware', rIdx, cIdx)}
                          className={`border-r border-slate-200 px-2 py-1.5 select-none cursor-cell overflow-hidden text-ellipsis whitespace-nowrap ${
                              HW_COL_PRINT_CLASSES[col.key] || ''
                          } ${
                            col.key === 'name_raw' || col.key === 'spec_raw' || col.key === 'notes' || col.key === 'color_hint' || col.key === 'item_type'
                              ? 'bom-wrap-text text-left'
                              : 'text-center'
                          } ${
                            active
                              ? `bg-blue-100 ring-2 ring-blue-600 ring-inset z-10 ${PRINT_CELL_SELECTION_RING}`
                              : selected
                                ? `bg-blue-50 ring-1 ring-blue-400 ring-inset ${PRINT_CELL_SELECTION_RING}`
                                : ''
                          }`}
                        >
                          {isEditingThis ? (
                            <input
                              ref={cellInputRef}
                              type={col.type === 'number' ? 'number' : 'text'}
                              defaultValue={(r as any)[col.key] || ''}
                              onBlur={(e) => {
                                handleCellChange('hardware', rIdx, cIdx, e.target.value)
                                setEditingCell(null)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleCellChange('hardware', rIdx, cIdx, (e.target as any).value)
                                  setEditingCell(null)
                                } else if (e.key === 'Escape') {
                                  setEditingCell(null)
                                }
                              }}
                              className="w-full rounded border border-blue-500 bg-white px-1 py-0.5 text-xs outline-none"
                            />
                          ) : (
                            cellContent
                          )}
                        </td>
                      )
                    })}

                    {/* Cột Vật tư ánh xạ (chỉ hiện trên màn hình, print:hidden) */}
                    <td className="border-r border-slate-200 px-2 py-1.5 print:hidden no-print text-xs">
                      {r.material_id ? (
                        <div className="flex items-center justify-between gap-1">
                          <div className="min-w-0">
                            <span className="font-mono font-bold text-blue-700 block truncate text-[11px]" title={r.material_code || r.material_id}>
                              {r.material_code || r.material_id}
                            </span>
                            {r.material_name && (
                              <span className="text-slate-600 block truncate text-[10px]" title={r.material_name}>
                                {r.material_name}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setMappingRowIndex(rIdx)}
                            className="p-1 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition shrink-0"
                            title="Đổi ánh xạ vật tư"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                            Chưa ánh xạ
                          </span>
                          <button
                            type="button"
                            onClick={() => setMappingRowIndex(rIdx)}
                            className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 hover:bg-blue-100 border border-blue-200 transition shrink-0"
                            title="Ánh xạ vật tư thật trong ERP"
                          >
                            <Link2 className="h-2.5 w-2.5" />
                            Ánh xạ
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Cột Đơn giá (chỉ hiện trên màn hình, print:hidden) */}
                    <td className="border-r border-slate-200 px-2 py-1.5 print:hidden no-print text-right font-mono text-xs whitespace-nowrap">
                      {hardwarePrices[r.id || String(rIdx)]?.status === 'MATCHED' && typeof hardwarePrices[r.id || String(rIdx)]?.unitPrice === 'number' ? (
                        <span className="font-semibold text-slate-800">
                          {hardwarePrices[r.id || String(rIdx)]!.unitPrice!.toLocaleString('vi-VN')} đ
                        </span>
                      ) : hardwarePrices[r.id || String(rIdx)]?.status === 'NEEDS_MAPPING' || !r.material_id ? (
                        <span className="text-amber-600 text-[11px] font-medium italic">Chưa ánh xạ</span>
                      ) : (
                        <span className="text-slate-400 text-[11px] italic">Chưa có giá</span>
                      )}
                    </td>

                    {/* Cột Thành tiền (chỉ hiện trên màn hình, print:hidden) */}
                    <td className="border-r border-slate-200 px-2 py-1.5 print:hidden no-print text-right font-mono text-xs font-bold text-slate-900 whitespace-nowrap">
                      {(() => {
                        const pr = hardwarePrices[r.id || String(rIdx)]
                        const q = typeof r.qty_per === 'number' ? r.qty_per : parseFloat(String(r.qty_per || '0')) || 0
                        if (pr?.status === 'MATCHED' && typeof pr.unitPrice === 'number') {
                          return `${Math.round(q * pr.unitPrice).toLocaleString('vi-VN')} đ`
                        }
                        return <span className="text-slate-400 font-normal">—</span>
                      })()}
                    </td>

                    <td className="text-center py-1 px-1 print:hidden no-print whitespace-nowrap">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveSingleRow('hardware', rIdx, 'up')}
                          disabled={rIdx === 0}
                          className={`rounded p-1 transition ${
                            rIdx === 0
                              ? 'opacity-30 cursor-not-allowed text-slate-400'
                              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                          }`}
                          title="Di chuyển dòng lên"
                          aria-label="Di chuyển dòng lên"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSingleRow('hardware', rIdx, 'down')}
                          disabled={rIdx === data.hardware.length - 1}
                          className={`rounded p-1 transition ${
                            rIdx === data.hardware.length - 1
                              ? 'opacity-30 cursor-not-allowed text-slate-400'
                              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                          }`}
                          title="Di chuyển dòng xuống"
                          aria-label="Di chuyển dòng xuống"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSingleRow('hardware', rIdx)}
                          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 transition"
                          title="Xóa dòng này"
                          aria-label="Xóa dòng này"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>

          {/* Tóm tắt chi phí ngũ kim (chỉ trên màn hình, print:hidden no-print) */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-4 py-2.5 print:hidden no-print">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                TỔNG HỢP CHI PHÍ NGŨ KIM:
              </span>
              <span className="rounded bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-800 border border-sky-200">
                Vật tư: {Math.round(totalHwMaterialCost).toLocaleString('vi-VN')} đ
              </span>
              <span className="rounded bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-200">
                VAT ({effectiveHwVatRate}%): {Math.round(totalHwVatAmount).toLocaleString('vi-VN')} đ
              </span>
              <span className="rounded bg-blue-700 px-3 py-1 font-mono text-xs font-black text-white shadow-xs">
                Tổng chi phí: {Math.round(grandTotalHwWithVat).toLocaleString('vi-VN')} đ
              </span>
            </div>
            <div className="text-[11px] text-slate-500">
              {mappedCount} / {data.hardware.length} dòng đã ánh xạ vật tư ERP
            </div>
          </div>
        </section>

        {/* ---------------- KHỐI CHỮ KÝ PHÊ DUYỆT TRÁCH NHIỆM ---------------- */}
        <section className="rounded-none border border-black bg-white p-4 shadow-none print:shadow-none print:border-black print:rounded-none print:break-inside-avoid">
          <div className="mx-auto grid max-w-2xl grid-cols-2 gap-6">
            <div className="flex h-36 flex-col justify-between rounded-none border border-black bg-white p-3 text-center print:bg-white print:border-black print:rounded-none">
              <div>
                <div className="text-xs font-black uppercase text-slate-900">
                  KỸ THUẬT LẬP BIỂU & KIỂM TRA
                </div>
                <div className="text-[11px] italic text-slate-500">
                  (Tính toán bóc tách & rà soát định mức)
                </div>
              </div>
              <div className="border-t border-solid border-black pt-2 font-serif text-lg font-bold italic text-emerald-800 print:text-black print:border-black print:border-solid">
                Hà Phan Thanh Tâm
              </div>
            </div>

            <div className="flex h-36 flex-col justify-between rounded-none border border-black bg-white p-3 text-center print:bg-white print:border-black print:rounded-none">
              <div>
                <div className="text-xs font-black uppercase text-slate-900">
                  BAN GIÁM ĐỐC DUYỆT
                </div>
                <div className="text-[11px] italic text-slate-500">
                  (Phê duyệt chính thức lệnh sản xuất)
                </div>
              </div>
              <div className="border-t border-solid border-black pt-2 font-serif text-lg font-bold italic text-blue-900 print:text-black print:border-black print:border-solid">
                Nguyễn Thị Ngọc
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- MODAL LỊCH SỬ PHIÊN BẢN ---------------- */}
      {showRevisionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-white shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-5 py-3.5 text-white">
              <div className="flex items-center gap-2.5">
                <History className="h-5 w-5 text-sky-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Lịch sử các phiên bản định mức (Revision History)
                  </h3>
                  <p className="text-[11px] text-slate-300">
                    Mã hàng: <strong className="text-white">{productCode}</strong> — {productName} | Cấu hình: {activeConfig.code}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowRevisionModal(false)}
                className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Subheader info */}
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-2 text-xs text-slate-600">
              <div className="flex items-center gap-4">
                <span>
                  Tổng số phiên bản: <strong className="text-slate-900">{revisions.length}</strong>
                </span>
                <span>
                  Phiên bản hiện tại:{' '}
                  <span className="rounded bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800 border border-emerald-300">
                    {currentRev.revLabel} ({currentRev.savedAt})
                  </span>
                </span>
              </div>
              <span className="text-[11px] text-slate-400">
                Tự động ghi nhận chi tiết từng ô sửa, dòng thêm/xóa khi bấm &quot;Lưu bảng BOM&quot;
              </span>
            </div>

            {/* Revision Timeline List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {[...revisions].reverse().map((rev, revIdx) => {
                const isLatest = rev.id === currentRev.id
                const isExpanded = expandedRevId === rev.id || (expandedRevId === null && revIdx === 0)
                const addedCount = rev.diffs.filter((d) => d.type === 'row_added').length
                const deletedCount = rev.diffs.filter((d) => d.type === 'row_deleted').length
                const editedCount = rev.diffs.filter((d) => d.type === 'cell_change').length

                return (
                  <div
                    key={rev.id || revIdx}
                    className={`rounded-lg border transition ${
                      isLatest
                        ? 'border-blue-300 bg-blue-50/20 shadow-xs ring-1 ring-blue-300'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="rounded bg-slate-900 px-2.5 py-1 font-mono text-xs font-black text-white">
                          {rev.revLabel}
                        </span>
                        {isLatest && (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800 border border-emerald-200">
                            🟢 Đang áp dụng (Mới nhất)
                          </span>
                        )}
                        <span className="text-xs text-slate-600">
                          Ngày sửa: <strong className="text-slate-800">{rev.savedAt}</strong>
                          {rev.savedTime && <span className="text-slate-400"> lúc {rev.savedTime}</span>}
                        </span>
                        <span className="text-xs text-slate-500">
                          Người lập: <strong className="text-slate-700">{rev.author}</strong>
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {!isLatest && (
                          <button
                            type="button"
                            onClick={() => handleRestoreRevision(rev)}
                            className="inline-flex items-center gap-1 rounded bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 border border-amber-300 hover:bg-amber-100 transition active:scale-95"
                            title="Khôi phục lại dữ liệu BOM của phiên bản này"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Khôi phục bản này
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedRevId(isExpanded ? '__none__' : rev.id)}
                          className="inline-flex items-center gap-1 rounded bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100"
                        >
                          <span>{rev.diffs.length} thay đổi</span>
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>

                    <div className="p-3.5">
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-semibold text-slate-700">Tóm tắt:</span>
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">
                          {rev.changesSummary || 'Khởi tạo dữ liệu'}
                        </span>
                        {addedCount > 0 && (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                            +{addedCount} dòng mới
                          </span>
                        )}
                        {deletedCount > 0 && (
                          <span className="rounded bg-red-100 px-2 py-0.5 font-bold text-red-800">
                            -{deletedCount} dòng xóa
                          </span>
                        )}
                        {editedCount > 0 && (
                          <span className="rounded bg-amber-100 px-2 py-0.5 font-bold text-amber-800">
                            {editedCount} ô cập nhật
                          </span>
                        )}
                      </div>

                      {/* Chi tiết từng thay đổi */}
                      {isExpanded && (
                        <div className="mt-3 rounded border border-slate-200 bg-slate-50/50 p-2.5">
                          {rev.diffs.length === 0 ? (
                            <div className="py-2 text-center text-xs italic text-slate-400">
                              Phiên bản gốc / khởi tạo ban đầu hoặc không có sai khác dữ liệu chi tiết.
                            </div>
                          ) : (
                            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                              {rev.diffs.map((diff, dIdx) => (
                                <div
                                  key={dIdx}
                                  className="flex items-start gap-2 rounded bg-white p-2 text-xs border border-slate-200 shadow-2xs"
                                >
                                  {diff.type === 'cell_change' && (
                                    <>
                                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                                        Sửa ô
                                      </span>
                                      <span className="font-semibold text-slate-700">
                                        [{diff.table === 'wood' ? 'Bảng Gỗ' : 'Bảng Ngũ kim'}] Dòng {diff.rowIndex} (
                                        <span className="text-slate-900">{diff.rowName}</span>) » Ô &quot;
                                        <span className="text-blue-700">{diff.fieldLabel || diff.field}</span>&quot;:
                                      </span>
                                      <div className="ml-auto flex items-center gap-1.5">
                                        <del className="rounded bg-red-50 px-1.5 py-0.5 font-mono text-red-700 border border-red-200">
                                          {diff.oldValue === '' || diff.oldValue === undefined
                                            ? '(trống)'
                                            : String(diff.oldValue)}
                                        </del>
                                        <span className="text-slate-400">➔</span>
                                        <ins className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono font-bold text-emerald-800 border border-emerald-200 no-underline">
                                          {diff.newValue === '' || diff.newValue === undefined
                                            ? '(xóa trắng)'
                                            : String(diff.newValue)}
                                        </ins>
                                      </div>
                                    </>
                                  )}

                                  {diff.type === 'row_added' && (
                                    <>
                                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                                        + Thêm dòng
                                      </span>
                                      <span className="text-slate-800">
                                        [{diff.table === 'wood' ? 'Bảng Gỗ' : 'Bảng Ngũ kim'}] Thêm dòng mới tại vị trí{' '}
                                        <strong>Dòng {diff.rowIndex}</strong>: &quot;
                                        <strong className="text-emerald-800">{diff.rowName}</strong>&quot;
                                      </span>
                                    </>
                                  )}

                                  {diff.type === 'row_deleted' && (
                                    <>
                                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-800">
                                        - Xóa dòng
                                      </span>
                                      <span className="text-slate-800">
                                        [{diff.table === 'wood' ? 'Bảng Gỗ' : 'Bảng Ngũ kim'}] Đã xóa dòng tại vị trí{' '}
                                        <strong>Dòng {diff.rowIndex}</strong>: &quot;
                                        <strong className="text-red-700">{diff.rowName}</strong>&quot;
                                      </span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-3">
              <span className="text-xs text-slate-500">
                Phiên bản định mức tự động tăng (Rev. 01 ➔ Rev. 02...) khi có thay đổi thực tế.
              </span>
              <button
                type="button"
                onClick={() => setShowRevisionModal(false)}
                className="rounded bg-slate-800 px-4 py-1.5 text-xs font-bold text-white hover:bg-slate-700 transition"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

            {/* Modal Ánh xạ vật tư ngũ kim */}
      {mappingRowIndex !== null && data.hardware[mappingRowIndex] && (
        <MaterialMappingModal
          isOpen={true}
          hardwareName={data.hardware[mappingRowIndex].name_raw}
          hardwareSpec={data.hardware[mappingRowIndex].spec_raw}
          currentMaterialId={data.hardware[mappingRowIndex].material_id}
          currentMaterialCode={data.hardware[mappingRowIndex].material_code}
          currentMaterialName={data.hardware[mappingRowIndex].material_name}
          onSelect={(m) => {
            const nextHardware = [...data.hardware]
            const row = { ...nextHardware[mappingRowIndex] }
            row.material_id = m.id
            row.material_code = m.code
            row.material_name = m.name
            if (m.uom_default) row.uom_hint = m.uom_default
            nextHardware[mappingRowIndex] = row
            const nextData = { ...data, hardware: nextHardware }
            setData(nextData)
            setMappingRowIndex(null)
          }}
          onUnmap={() => {
            const nextHardware = [...data.hardware]
            const row = { ...nextHardware[mappingRowIndex] }
            row.material_id = null
            row.material_code = null
            row.material_name = null
            nextHardware[mappingRowIndex] = row
            const nextData = { ...data, hardware: nextHardware }
            setData(nextData)
            setMappingRowIndex(null)
          }}
          onClose={() => setMappingRowIndex(null)}
        />
      )}

      {/* Modal So sánh cấu hình BOM */}
      <ProductBomSpreadsheetCompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        productId={productId || ''}
        productCode={productCode}
        productName={productName}
        configs={allConfigs}
        currentConfigId={activeConfig.id}
        currentData={data}
      />

      {/* Toast notifications */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 rounded-md bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white shadow-2xl animate-in fade-in slide-in-from-bottom-2 print:hidden">
          {toastMsg}
        </div>
      )}
    </div>
  )
}
